import axios from 'axios';
import { REACT_APP_BASE_URL_TEMPEDGE, REACT_APP_URL_DICTIONARY_LIST_ALL } from '../components/common/Routes/URLs';
import { applyDebugIp } from '../utils/helpers/debugIpOverride';
import { safeJsonParse } from '../utils/helpers/safeJson';
import { isSessionNotValidResponse, isSessionReplacedResponse } from '../utils/helpers/sessionErrors';
import {
  canRequestReauth,
  endSession,
  ensureFreshToken,
  isRefreshNetworkFailure,
  REAUTH_REASON,
  REFRESH_FAILURE_NETWORK,
  REFRESH_OUTCOMES,
  refreshOnce,
  requestReauth,
  takeRefreshFailureReason
} from '../utils/helpers/sessionManager';
import { getAccessToken, getDeviceHeaders, getTokenVersion } from '../utils/helpers/sessionStore';
import { BACKEND_IDS, getApiTimeoutMs, getBackendConfig, getDefaultBackend } from './ApiConfig';

const ApiServices = axios.create({
  baseURL: REACT_APP_BASE_URL_TEMPEDGE,
  timeout: getApiTimeoutMs(),
  headers: {
    'Content-Type': 'application/json'
  }
});

const backend2Config = getBackendConfig(BACKEND_IDS.BACKEND_2);
const ApiServices2 = axios.create({
  baseURL: backend2Config.baseURL,
  timeout: backend2Config.timeout,
  headers: backend2Config.headers
});

const getApiInstance = (backendType) => {
  return backendType === BACKEND_IDS.BACKEND_2 ? ApiServices2 : ApiServices;
};

/**
 * Endpoints that authenticate the caller and must never carry a Bearer token or
 * an injected orgId. The v2 paths are listed explicitly because they do not sit
 * under a bare `/oauth/` prefix.
 */
const OAUTH_ENDPOINTS = ['/oauth/token', '/oauth/check_token', '/oauth/refreshtoken', '/oauth/validate'];

/**
 * Endpoints anyone can call, with or without a session. They skip the interceptors altogether: no token
 * goes out (a stored one may be long expired), no refresh is waited for, no organization is added, and a
 * 401 never opens the reauthentication screen. Add a path here to make another endpoint public.
 */
export const PUBLIC_ENDPOINTS = [REACT_APP_URL_DICTIONARY_LIST_ALL];

const METHODS_REQUIRING_ORG_ID = ['post'];

// Backend v2 takes the orgId from the token, so these endpoints are sent without it.
const ENDPOINTS_WITHOUT_ORG_ID = ['/api/v2/invoice/'];

const skipsOrgId = (url) => ENDPOINTS_WITHOUT_ORG_ID.some((prefix) => url?.includes(prefix));

const getUrlPath = (url) => (url ? url.split('?')[0].split('#')[0] : '');

const isPublicEndpoint = (url) => PUBLIC_ENDPOINTS.includes(getUrlPath(url));

const isOAuthEndpoint = (url) => {
  if (!url) return false;

  const urlPath = getUrlPath(url);
  if (urlPath.startsWith('/oauth/')) return true;

  return OAUTH_ENDPOINTS.some((endpoint) => urlPath === endpoint || urlPath.endsWith(endpoint));
};

const shouldInjectOrgId = (config, isFormData) => {
  const method = config.method?.toLowerCase();

  return (
    METHODS_REQUIRING_ORG_ID.includes(method) &&
    !isOAuthEndpoint(config.url) &&
    !isPublicEndpoint(config.url) &&
    !skipsOrgId(config.url) &&
    !isFormData &&
    !Array.isArray(config.data)
  );
};

/**
 * Adds the organization to a JSON body. A replayed request carries the body axios already
 * serialized on the first attempt, a string: spreading it would turn every character into a key
 * and send the backend a corrupt body, so it is parsed back first and left alone if it is not JSON.
 * @param {object|string} data - Body of the request, as an object or as the JSON text of a replay.
 * @param {number} orgId - Organization to add when the body does not name one.
 * @returns {object|string} The body with the organization, or the original body when it cannot take one.
 * @author armandoricosandoval -ai-
 */
const withOrgId = (data, orgId) => {
  const body = typeof data === 'string' ? safeJsonParse(data) : data || {};

  return body && typeof body === 'object' ? { orgId, ...body } : data;
};

const stripContentTypeForFormData = (config) => {
  if (!config.headers) config.headers = {};

  delete config.headers['Content-Type'];
  ['common', 'post', 'put'].forEach((scope) => {
    if (config.headers[scope]) delete config.headers[scope]['Content-Type'];
  });
};

/**
 * Attaches the token and stamps the generation it belongs to. Awaiting
 * `ensureFreshToken` is what makes the interceptor an implicit request queue:
 * while a refresh is in flight every outgoing call waits for it instead of
 * leaving with a token the server is about to reject.
 */
const setupRequestInterceptor = (instance) => {
  instance.interceptors.request.use(
    async (config) => {
      const isFormData = config.data instanceof FormData;
      if (isFormData) stripContentTypeForFormData(config);

      if (!isOAuthEndpoint(config.url) && !isPublicEndpoint(config.url)) {
        const token = await ensureFreshToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          config.tokenVersion = getTokenVersion();
        }
      }

      applyDebugIp(Object.assign(config.headers, getDeviceHeaders()));

      if (shouldInjectOrgId(config, isFormData)) {
        const orgId = safeJsonParse(localStorage.getItem('agency'))?.organizationEntity?.orgId;
        if (orgId) config.data = withOrgId(config.data, orgId);
      }

      return config;
    },
    (error) => Promise.reject(error)
  );
};

setupRequestInterceptor(ApiServices);
setupRequestInterceptor(ApiServices2);

/**
 * Backend v1 answers some authentication failures with HTTP 200 and a 401 in the body. Only that status
 * marks a session failure: the codes of a session error (TE-E07, TE-E09) can also come with another
 * status, such as the 409 of a duplicated employee, and those are answers for the screen, not for the session.
 */
const isUnauthenticatedBody = (response) => response?.data?.status === 401;

const isUnauthorized = (error) => error?.response?.status === 401;

// Resolves to null when there is no token or the replay is refused too; the caller decides what that means.
const replayWithCurrentToken = async (instance, request) => {
  const token = getAccessToken();
  if (!token) return null;

  request.headers.Authorization = `Bearer ${token}`;
  request.tokenVersion = getTokenVersion();

  try {
    return await instance(request);
  } catch (error) {
    if (isUnauthorized(error)) return null;
    throw error;
  }
};

/** Outcomes that leave a token worth replaying the request with. */
const canRetryAfter = (outcome) => outcome === REFRESH_OUTCOMES.REFRESHED || outcome === REFRESH_OUTCOMES.UP_TO_DATE;

const NETWORK_REPLAY_RETRIES = 1;
const NETWORK_REPLAY_DELAY_MS = 500;

const pause = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

/**
 * Renews the token for a refused request. A refresh skipped by the network backoff runs once for real, since a
 * lost reply left the server with rotated tokens it can still renew. When the server cannot be reached, it is
 * tried once more after a short pause before the caller concludes that the connection, not the session, is gone.
 * @returns {Promise<string>} One of the refresh outcomes.
 * @author armandoricosandoval -ai-
 */
const refreshForReplay = async () => {
  let outcome = await refreshOnce();
  if (outcome === REFRESH_OUTCOMES.DEFERRED) outcome = await refreshOnce({ ignoreBackoff: true });

  for (
    let attempt = 0;
    attempt < NETWORK_REPLAY_RETRIES && outcome === REFRESH_OUTCOMES.DEFERRED && isRefreshNetworkFailure();
    attempt += 1
  ) {
    await pause(NETWORK_REPLAY_DELAY_MS);
    outcome = await refreshOnce({ ignoreBackoff: true });
  }

  return outcome;
};

// A 401 gets one chance to be a stale generation: replay with the newest token, refreshing first when none is newer.
// TE-E09 (session not valid) includes an access token left behind by a refresh, so it takes the same path.
// When the refresh cannot recover it, the refresh body decides the screen: TE-E07 another device, otherwise this session is not valid.
// A replay refused after a good refresh is the same session-not-valid case: the API also answers it when the IP changed.
const reasonAfterFailedRefresh = (rejected) => {
  const refreshReason = takeRefreshFailureReason();
  if (refreshReason === REFRESH_FAILURE_NETWORK) return refreshReason;
  if (refreshReason === REAUTH_REASON.sessionReplaced) return refreshReason;
  if (isSessionNotValidResponse(rejected)) return REAUTH_REASON.sessionNotValid;

  return refreshReason || REAUTH_REASON.unauthorized;
};

const replayWithNewerToken = async (instance, request, rejected) => {
  if ((request.tokenVersion ?? -1) < getTokenVersion()) {
    return { replayed: await replayWithCurrentToken(instance, request), reason: REAUTH_REASON.unauthorized };
  }

  if (!canRetryAfter(await refreshForReplay())) return { replayed: null, reason: reasonAfterFailedRefresh(rejected) };

  return { replayed: await replayWithCurrentToken(instance, request), reason: REAUTH_REASON.sessionNotValid };
};

// The user logs in again on top of the screen and the refused request is replayed, so nothing on screen is lost.
const replayAfterReauth = async (instance, request, error, reason) => {
  if (!canRequestReauth()) {
    endSession('unauthorized');
    throw error;
  }

  const replayed = (await requestReauth(reason)) ? await replayWithCurrentToken(instance, request) : null;
  if (replayed) return replayed;

  endSession('unauthorized_after_reauth');
  throw error;
};

/**
 * The API said outright that the account was opened on another device, so there is
 * nothing to diagnose: the user signs in again on top of the screen and the request
 * is replayed. The answer is returned as is when the user gives up or cannot sign in.
 */
const reauthAfterReplacedResponse = async (instance, response) => {
  const { config } = response;
  config._retried = true;

  const replayed =
    canRequestReauth() && (await requestReauth(REAUTH_REASON.sessionReplaced)) ? await replayWithCurrentToken(instance, config) : null;
  return replayed || response;
};

const setupResponseInterceptor = (instance) => {
  instance.interceptors.response.use(
    async (response) => {
      if (response.config?._retried || isPublicEndpoint(response.config?.url)) return response;
      if (!isUnauthenticatedBody(response)) return response;
      if (isSessionReplacedResponse(response)) return reauthAfterReplacedResponse(instance, response);

      response.config._retried = true;
      const retried = canRetryAfter(await refreshOnce()) ? await replayWithCurrentToken(instance, response.config) : null;
      return retried || response;
    },
    async (error) => {
      const request = error.config;

      if (!isUnauthorized(error) || !request || request._retried || request.skipSessionRecovery || isPublicEndpoint(request.url)) {
        return Promise.reject(error);
      }

      request._retried = true;

      if (isSessionReplacedResponse(error.response)) return replayAfterReauth(instance, request, error, REAUTH_REASON.sessionReplaced);

      const { replayed, reason } = await replayWithNewerToken(instance, request, error.response);
      if (replayed) return replayed;
      return replayAfterReauth(instance, request, error, reason === REFRESH_FAILURE_NETWORK ? REAUTH_REASON.connectionLost : reason);
    }
  );
};

setupResponseInterceptor(ApiServices);
setupResponseInterceptor(ApiServices2);

/**
 * Get API service instance based on backend type.
 * @param {string} backendType - Backend type (defaults to configured default).
 * @returns {AxiosInstance} Configured axios instance.
 */
export const getApiService = (backendType = getDefaultBackend()) => {
  return getApiInstance(backendType);
};

export default ApiServices;

export { ApiServices2 };
